import { useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAllServices, useCreateService, useUpdateService, useDeleteService, Service } from "@/hooks/useServices";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Edit2, Trash2, ArrowUp, ArrowDown, Eye, Upload } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { toast } from "sonner";

const ServicesSection = () => {
  const { data: services, isLoading } = useAllServices();
  const createService = useCreateService();
  const updateService = useUpdateService();
  const deleteService = useDeleteService();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [deleteServiceId, setDeleteServiceId] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Ընտրեք նկարի ֆայլ");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Ֆայլը չպետք է գերազանցի 5MB");
      return;
    }

    setIsUploadingImage(true);
    try {
      const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const path = `services/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${extension}`;
      const { error } = await supabase.storage
        .from("course-images")
        .upload(path, file, { cacheControl: "3600", upsert: false });
      if (error) throw error;

      const { data } = supabase.storage.from("course-images").getPublicUrl(path);
      setFormData((prev) => ({ ...prev, image_url: data.publicUrl }));
      toast.success("Նկարը բեռնվեց");
    } catch (error) {
      console.error("Service image upload failed:", error);
      toast.error("Չհաջողվեց բեռնել նկարը");
    } finally {
      setIsUploadingImage(false);
    }
  };

  const [formData, setFormData] = useState<{
    title: string;
    title_en: string;
    title_ru: string;
    slug: string;
    description: string;
    content: string;
    icon: string;
    image_url: string;
    features: string[];
    pricing_info: {
      starting_from?: string;
      currency?: string;
      note?: string;
    };
    color: string;
    is_active: boolean;
  }>({
    title: "",
    title_en: "",
    title_ru: "",
    slug: "",
    description: "",
    content: "",
    icon: "Briefcase",
    image_url: "",
    features: [""],
    pricing_info: {
      starting_from: "",
      currency: "AMD",
      note: "",
    },
    color: "#10b981",
    is_active: true,
  });

  const availableIcons = [
    "Globe", "Palette", "Smartphone", "Code", "Layout", "Package",
    "Briefcase", "Monitor", "Cloud", "Database", "Settings", "Zap"
  ];

  const handleSubmit = async () => {
    if (!formData.title || !formData.slug) {
      toast.error("Լրացրեք պարտադիր դաշտերը");
      return;
    }

    const serviceData = {
      ...formData,
      features: formData.features.filter(f => f.trim() !== ""),
      pricing_info: formData.pricing_info,
    };

    if (editingService) {
      await updateService.mutateAsync({ id: editingService.id, ...serviceData });
    } else {
      await createService.mutateAsync(serviceData);
    }

    resetForm();
    setIsDialogOpen(false);
  };

  const handleEdit = (service: Service) => {
    setEditingService(service);
    setFormData({
      title: service.title,
      title_en: service.title_en || "",
      title_ru: service.title_ru || "",
      slug: service.slug,
      description: service.description || "",
      content: service.content || "",
      icon: service.icon,
      image_url: service.image_url || "",
      features: service.features.length > 0 ? service.features : [""],
      pricing_info: service.pricing_info || {
        starting_from: "",
        currency: "AMD",
        note: "",
      },
      color: service.color,
      is_active: service.is_active,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    await deleteService.mutateAsync(id);
    setDeleteServiceId(null);
  };

  const handleReorder = async (service: Service, direction: "up" | "down") => {
    if (!services) return;

    const currentIndex = services.findIndex((s) => s.id === service.id);
    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= services.length) return;

    const otherService = services[newIndex];

    await Promise.all([
      updateService.mutateAsync({
        id: service.id,
        order_index: otherService.order_index,
      }),
      updateService.mutateAsync({
        id: otherService.id,
        order_index: service.order_index,
      }),
    ]);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      title_en: "",
      title_ru: "",
      slug: "",
      description: "",
      content: "",
      icon: "Briefcase",
      image_url: "",
      features: [""],
      pricing_info: {
        starting_from: "",
        currency: "AMD",
        note: "",
      },
      color: "#10b981",
      is_active: true,
    });
    setEditingService(null);
  };

  const addFeature = () => {
    setFormData({ ...formData, features: [...formData.features, ""] });
  };

  const updateFeature = (index: number, value: string) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  const removeFeature = (index: number) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: newFeatures });
  };

  return (
    <Card className="p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <h2 className="text-2xl font-bold">Ծառայություններ</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="h-4 w-4 mr-2" />
              Ավելացնել ծառայություն
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingService ? "Խմբագրել ծառայությունը" : "Նոր ծառայություն"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {/* Title */}
              <div>
                <Label htmlFor="title">Անվանում (հայերեն)*</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Վեբ կայքերի պատրաստում"
                />
              </div>

              {/* Translations */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title_en">Անվանում (English)</Label>
                  <Input
                    id="title_en"
                    value={formData.title_en}
                    onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="title_ru">Անվանում (Русский)</Label>
                  <Input
                    id="title_ru"
                    value={formData.title_ru}
                    onChange={(e) => setFormData({ ...formData, title_ru: e.target.value })}
                  />
                </div>
              </div>

              {/* Slug */}
              <div>
                <Label htmlFor="slug">Slug (URL)*</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="web-development"
                />
              </div>

              {/* Icon and Color */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="icon">Իկոն</Label>
                  <select
                    id="icon"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  >
                    {availableIcons.map((icon) => (
                      <option key={icon} value={icon}>
                        {icon}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="color">Գույն</Label>
                  <Input
                    id="color"
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">Կարճ նկարագրություն</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                />
              </div>

              {/* Content */}
              <div>
                <Label htmlFor="content">Մանրամասն բովանդակություն (Markdown)</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={6}
                />
              </div>

              {/* Image */}
              <div>
                <Label htmlFor="image_url">Նկար</Label>
                <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-start">
                  {formData.image_url && (
                    <img
                      src={formData.image_url}
                      alt="Ծառայության նկար"
                      className="h-24 w-24 shrink-0 rounded-md border border-border/70 bg-muted object-contain p-1"
                    />
                  )}
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <Input
                      id="image_url"
                      placeholder="Նկարի URL"
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageFileChange}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        disabled={isUploadingImage}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        {isUploadingImage ? "Բեռնվում է..." : "Ավելացնել համակարգչից"}
                      </Button>
                      {formData.image_url && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => setFormData({ ...formData, image_url: "" })}
                        >
                          Հեռացնել
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      PNG, JPG, WEBP կամ SVG՝ մինչև 5MB
                    </p>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div>
                <Label>Առանձնահատկություններ</Label>
                {formData.features.map((feature, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <Input
                      value={feature}
                      onChange={(e) => updateFeature(index, e.target.value)}
                      placeholder="Օր․ Responsive դիզայն"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      onClick={() => removeFeature(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={addFeature}>
                  <Plus className="h-4 w-4 mr-2" />
                  Ավելացնել
                </Button>
              </div>

              {/* Pricing */}
              <div className="space-y-2">
                <Label>Գնային տեղեկություն</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Input
                      value={formData.pricing_info.starting_from}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          pricing_info: {
                            ...formData.pricing_info,
                            starting_from: e.target.value,
                          },
                        })
                      }
                      placeholder="Սկսած (օր․ 300000)"
                    />
                  </div>
                  <div>
                    <Input
                      value={formData.pricing_info.currency}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          pricing_info: {
                            ...formData.pricing_info,
                            currency: e.target.value,
                          },
                        })
                      }
                      placeholder="Արժույթ (օր․ AMD)"
                    />
                  </div>
                </div>
                <Input
                  value={formData.pricing_info.note}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      pricing_info: {
                        ...formData.pricing_info,
                        note: e.target.value,
                      },
                    })
                  }
                  placeholder="Նշում (օր․ Գինը կախված է բարդությունից)"
                />
              </div>

              {/* Active Status */}
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_active: checked })
                  }
                />
                <Label htmlFor="is_active">Ակտիվ</Label>
              </div>

              <Button onClick={handleSubmit} className="w-full">
                {editingService ? "Թարմացնել" : "Ավելացնել"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Իկոն</TableHead>
              <TableHead>Անվանում</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Կարգավիճակ</TableHead>
              <TableHead className="text-right">Գործողություններ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center">
                  Բեռնվում է...
                </TableCell>
              </TableRow>
            ) : services && services.length > 0 ? (
              services.map((service, index) => {
                const IconComponent = (LucideIcons as any)[service.icon] || LucideIcons.Briefcase;
                return (
                  <TableRow key={service.id}>
                    <TableCell>
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{
                          backgroundColor: `${service.color}20`,
                          color: service.color,
                        }}
                      >
                        <IconComponent className="w-5 h-5" />
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{service.title}</TableCell>
                    <TableCell className="text-muted-foreground">{service.slug}</TableCell>
                    <TableCell>
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          service.is_active
                            ? "bg-green-500/10 text-green-600 dark:text-primary dark:bg-green-900 dark:text-muted-foreground"
                            : "bg-muted text-foreground dark:bg-card dark:text-foreground"
                        }`}
                      >
                        {service.is_active ? "Ակտիվ" : "Անջատված"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => window.open(`/services/${service.slug}`, "_blank")}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleReorder(service, "up")}
                          disabled={index === 0}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleReorder(service, "down")}
                          disabled={index === services.length - 1}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleEdit(service)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setDeleteServiceId(service.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  Ծառայություններ չկան
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!deleteServiceId} onOpenChange={() => setDeleteServiceId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ջնջե՞լ ծառայությունը</AlertDialogTitle>
            <AlertDialogDescription>
              Այս գործողությունը անհետադարձելի է։
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Չեղարկել</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteServiceId && handleDelete(deleteServiceId)}
            >
              Ջնջել
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default ServicesSection;
